import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useSocket } from '@/contexts/SocketContext';
import { agentAPI } from '../api/agent.api';
import type { PlanFiltersState } from '../types/agent.types';

export const useAgentPlans = (mode: 'pending' | 'history', filters: PlanFiltersState) => {
  const queryClient = useQueryClient();
  const { socket } = useSocket();

  const queryParams: Record<string, string> = {};
  if (filters.role !== 'all') queryParams.role = filters.role;
  if (filters.planType) queryParams.plan_type = filters.planType;
  if (filters.priority) queryParams.priority = filters.priority;
  if (filters.search) queryParams.search = filters.search;
  if (filters.statusFilter) queryParams.status = filters.statusFilter;

  const fetchFn = mode === 'history'
    ? () => agentAPI.getHistory(queryParams as any)
    : () => agentAPI.getPlans(queryParams);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['agent-plans', mode, filters],
    queryFn: fetchFn,
    staleTime: 10000,
    gcTime: 30000,
  });

  // WebSocket updates
  useEffect(() => {
    if (!socket) return;

    const handleUpdate = () => {
      queryClient.invalidateQueries({ queryKey: ['agent-plans'] });
      queryClient.invalidateQueries({ queryKey: ['agent-pending-count'] });
    };

    socket.on('agent:plan:created', handleUpdate);
    socket.on('agent:plan:updated', handleUpdate);
    socket.on('agent:plan:cancelled', handleUpdate);
    socket.on('agent:plan:completed', handleUpdate);
    socket.on('agent:plan:failed', handleUpdate);

    return () => {
      socket.off('agent:plan:created', handleUpdate);
      socket.off('agent:plan:updated', handleUpdate);
      socket.off('agent:plan:cancelled', handleUpdate);
      socket.off('agent:plan:completed', handleUpdate);
      socket.off('agent:plan:failed', handleUpdate);
    };
  }, [socket, queryClient]);

  return {
    plans: data?.data || [],
    isLoading,
    error,
    refetch,
  };
};
